import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const services = [
  {
    icon: "ri-film-line",
    title: "影视资源聚合",
    desc: "聚合电影、电视剧、综艺等多元内容，提供封面、简介、主演、导演、地区、年份等完整资料。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维筛选浏览",
    desc: "支持按类型、地区、年份筛选与多种排序方式，帮助您快速定位想看的内容。",
  },
  {
    icon: "ri-search-line",
    title: "快捷内容搜索",
    desc: "通过关键词即可在影视库中进行模糊匹配，快速找到特定影片或感兴趣的作品。",
  },
  {
    icon: "ri-heart-line",
    title: "收藏与记录",
    desc: "无需登录即可收藏喜欢的作品，并自动记录浏览历史，方便随时回顾。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - 我的家庭教师",
    description:
      "了解「我的家庭教师」影视频道的定位与服务：专注家庭观影场景，聚合电影、电视剧、综艺资源，提供筛选、搜索、收藏等便捷体验。",
    keywords: "关于我的家庭教师,影视频道介绍,家庭教师影视,影视门户,联系方式",
    path: "/about",
  });

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-accent-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">关于我们</span>
      </nav>

      <header className="mt-6 grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-accent-500/40 bg-accent-500/10 px-3 py-1 text-xs font-medium text-accent-400">
            <i className="ri-emotion-happy-line text-sm"></i>
            关于我的家庭教师
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-wide text-foreground-950 md:text-4xl">
            专注家庭观影的影视内容门户
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            「我的家庭教师」是一个面向普通观众的影视内容平台，专注于电影、电视剧、综艺等资源的内容展示与发现。
            我们希望用清晰的分类、详尽的资料与顺手的工具，降低找片成本，让每一次家庭观影都更从容、更有温度。
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/category/${encodeURIComponent("电影")}`}
              className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-movie-line text-base"></i>
              浏览影视库
            </Link>
            <Link
              to="/search"
              className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 px-6 text-sm font-medium text-foreground-800 transition-colors hover:border-accent-500/60 hover:text-accent-400"
            >
              <i className="ri-search-line text-base"></i>
              搜索影视
            </Link>
          </div>
        </div>
        <div className="overflow-hidden rounded-lg border border-background-200/70">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20home%20cinema%20setup%20with%20large%20screen%20and%20soft%20ambient%20backlight%2C%20deep%20navy%20and%20warm%20orange%20color%20grading%2C%20cozy%20premium%20interior%2C%20cinematic%20mood%2C%20no%20text%2C%20no%20letters%2C%20high%20detail%20editorial%20photography&width=900&height=640&seq=about-hero-01&orientation=landscape"
            alt="我的家庭教师 家庭影音体验"
            title="我的家庭教师 影视资源"
            className="h-56 w-full object-cover object-top md:h-72"
          />
        </div>
      </header>

      <section className="mt-14">
        <h2 className="font-heading text-2xl font-semibold tracking-wide text-foreground-950">
          我们提供什么
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s) => (
            <div
              key={s.title}
              className="rounded-lg border border-background-200/70 bg-background-100 p-5 transition-colors hover:border-primary-400/60"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500 text-foreground-950">
                <i className={`${s.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                {s.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-background-200/70 bg-background-100 p-6">
          <h2 className="font-heading text-xl font-semibold tracking-wide text-foreground-950">
            联系方式
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            如有内容合作、反馈建议或权益事宜，欢迎通过以下方式与我们联系。
          </p>
          <ul className="mt-5 space-y-3.5 text-sm">
            <li className="flex items-center gap-3 text-foreground-800">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500/15 text-accent-400">
                <i className="ri-mail-line text-base"></i>
              </span>
              合作邮箱：support@familytutor-media.cn
            </li>
            <li className="flex items-center gap-3 text-foreground-800">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500/15 text-accent-400">
                <i className="ri-customer-service-2-line text-base"></i>
              </span>
              客服电话：400-800-1234（工作日 9:00 - 18:00）
            </li>
            <li className="flex items-center gap-3 text-foreground-800">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500/15 text-accent-400">
                <i className="ri-map-pin-line text-base"></i>
              </span>
              办公地址：北京市朝阳区影视文化产业园 A 座 12 层
            </li>
          </ul>
          <div className="mt-5 flex flex-wrap gap-3">
            {[
              { icon: "ri-wechat-line", label: "微信" },
              { icon: "ri-weibo-line", label: "微博" },
              { icon: "ri-bilibili-line", label: "哔哩哔哩" },
              { icon: "ri-youtube-line", label: "视频号" },
            ].map((s) => (
              <span
                key={s.label}
                className="flex items-center gap-2 rounded-md border border-background-300/60 px-3 py-2 text-xs text-foreground-700 transition-colors hover:border-accent-500/60 hover:text-accent-400"
              >
                <i className={`${s.icon} text-base`}></i>
                {s.label}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-background-200/70 bg-background-100 p-6">
          <h2 className="font-heading text-xl font-semibold tracking-wide text-foreground-950">
            版权与免责声明
          </h2>
          <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
            <li className="flex gap-2">
              <i className="ri-shield-check-line mt-0.5 shrink-0 text-base text-accent-400"></i>
              本站为影视内容资讯门户，站内影视资料（包括海报、简介等）仅用于信息展示与学习交流，版权归原权利人所有。
            </li>
            <li className="flex gap-2">
              <i className="ri-shield-check-line mt-0.5 shrink-0 text-base text-accent-400"></i>
              本站不提供任何影视资源的存储与在线播放服务，相关内容均来自公开信息整理。
            </li>
            <li className="flex gap-2">
              <i className="ri-shield-check-line mt-0.5 shrink-0 text-base text-accent-400"></i>
              若权利人对本站内容有异议，请通过上方联系方式与我们取得联系，我们将第一时间核实处理。
            </li>
            <li className="flex gap-2">
              <i className="ri-shield-check-line mt-0.5 shrink-0 text-base text-accent-400"></i>
              收藏与浏览记录功能基于浏览器本地存储实现，数据仅保存在您的设备中，不会上传服务器。
            </li>
          </ul>
        </div>
      </section>

      <p className="mt-10 text-center text-xs text-foreground-500">
        © {new Date().getFullYear()} 我的家庭教师 · 影视频道 保留所有权利
      </p>
    </div>
  );
}